/**
 * This is loaded by ./preload.js
 */
import 'virtual:uno.css'
import '@unocss/reset/tailwind-compat.css'

import { getEnv, setEnv } from '@monogrid/env'
import type { CMSAuthInjectionKeyType } from '@monogrid/vue-boost/cms'
import type { GetTranslationsInjectionKeyType, I18nInjectionKeyType } from '@monogrid/vue-boost/i18n'
import { createHead } from '@unhead/vue'
import type { InjectionKey } from 'vue'
import { createApp } from 'vue'

import base from '../config/base.mjs'
import App from './App.vue'
import { useBreakpoint } from './composables/useBreakpoint'
import directives from './directives'
import type { PreloaderPublicInstance } from './preload.ts'
import { routes, setupRouter } from './router'

/**
 * global injection keys
 * we provide types only so they can be referenced
 */
export type GlobalInjectionKeys = {
  cmsAuth?: CMSAuthInjectionKeyType
  getTranslations?: GetTranslationsInjectionKeyType
  i18n?: I18nInjectionKeyType
  preloader: InjectionKey<PreloaderPublicInstance>
}

export const globalInjectionKeys: GlobalInjectionKeys = {
  preloader: Symbol('preloader')
}

const boostrap = async () => {
  setEnv(DOT_ENV)
  const env = getEnv()

  const app = createApp(App)
  app.config.globalProperties.$breakpoints = useBreakpoint()

  app.use(createHead()) // https://github.com/vueuse/head

  app.provide(globalInjectionKeys.preloader, window.preloaderInstance) // TODO: Fix this horrible hack

  let routerRoutes = routes
  let addRouterHooks

  // add optional internationalization support
  if (USE_I18N) {
    const { default: translations } = await import('../config/translations/index.mjs')

    const {
      setTranslations,
      setupI18n,
      i18nInjectionKey,
      i18n,
      getTranslationsInjectionKey,
      useLocalizedRoutes,
      addRouterHooks: i18nAddRouterHooks,
      getTranslations
    } = await import('@monogrid/vue-boost/i18n')

    setTranslations(translations)

    app.use(await setupI18n())
    app.provide(i18nInjectionKey, i18n)
    app.provide(getTranslationsInjectionKey, getTranslations)

    routerRoutes = useLocalizedRoutes(routes)
    addRouterHooks = i18nAddRouterHooks

    globalInjectionKeys.i18n = i18nInjectionKey
    globalInjectionKeys.getTranslations = getTranslationsInjectionKey
  }

  // add optional CMS support
  if (USE_CMS) {
    const clientModule = await import('@monogrid/vue-boost/urql-client')
    const urlParams = (new URL(document.location.href)).searchParams
    const customExchanges = []
    /** @type {typeof import("@monogrid/vue-boost/cms")} */
    let cmsModule

    if (urlParams.get('m-cms-preview') === '1') {
      cmsModule = await import('@monogrid/vue-boost/cms')
      customExchanges.push(
        cmsModule.cmsAuthExchange,
        cmsModule.cmsContentPreviewExchange
      )
      app.use(cmsModule.CMSAuthPlugin)
      app.provide(cmsModule.CMSAuthInjectionKey, cmsModule.CMSAuth)
      globalInjectionKeys.cmsAuth = cmsModule.CMSAuthInjectionKey
    }

    const { client, urql } = clientModule.provideClient({ customExchanges, endpoint: `${env.CONTENT_API_ENDPOINT}` })
    app.use(urql, client)
    await cmsModule?.CMSAuth.bootstrap({ graphQLClient: app._context.provides.$urql.value })
  }

  // custom directives
  Object.entries(directives).forEach(([key, config]) => app.directive(key, config))

  // https://next.router.vuejs.org/guide/
  const router = setupRouter(routerRoutes, { base, addRouterHooks })

  // https://next.router.vuejs.org/guide/
  app.use(router)

  await router.isReady()

  let lastWarning
  app.config.warnHandler = (msg, instance, trace) => {
    if (lastWarning === msg) return
    console.warn('[Vue warn]: '.concat(msg).concat(trace))
    lastWarning = msg
  }

  app.mount('#app')

  return app
}

if (window.app && typeof window.app.unmount === 'function') {
  window.app.unmount()
}

boostrap()
  .then(app => (window.app = app))
  .catch(error => console.error(error))
